import { PaginatedEvent } from '~/components/activity/types'

// ClickHouse ingests asynchronously, so subscription-delivered events can be missing from reads
// for a short window. Persist them per scope until the TTL expires so they survive a refresh.
const STORAGE_KEY = 'doss.activityFeed.recentEvents'
const EVENT_TTL_MS = 15 * 60 * 1000
const MAX_EVENTS_PER_SCOPE = 50

interface CachedEntry {
  cachedAtMs: number
  event: PaginatedEvent
}

type EventCache = Record<string, CachedEntry[]>

const readCache = (): EventCache => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as EventCache) : {}
  } catch {
    return {}
  }
}

const writeCache = (cache: EventCache): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cache))
  } catch {
    // Best-effort: losing the cache only re-exposes the eventual-consistency window.
  }
}

const pruneExpired = (cache: EventCache): EventCache => {
  const nowMs = Date.now()
  const entries = Object.entries(cache)
    .map(
      ([scopeKey, scopeEntries]) =>
        [
          scopeKey,
          scopeEntries.filter((entry) => nowMs - entry.cachedAtMs < EVENT_TTL_MS),
        ] as const,
    )
    .filter(([, scopeEntries]) => scopeEntries.length > 0)
  return Object.fromEntries(entries)
}

export const cacheRecentEvent = (scopeKey: string, event: PaginatedEvent): void => {
  const cache = pruneExpired(readCache())
  const scopeEntries = (cache[scopeKey] ?? []).filter(
    (entry) => entry.event.event.id !== event.event.id,
  )
  scopeEntries.push({ cachedAtMs: Date.now(), event })
  cache[scopeKey] = scopeEntries.slice(-MAX_EVENTS_PER_SCOPE)
  writeCache(cache)
}

export const readRecentEvents = (scopeKey: string): PaginatedEvent[] => {
  const cache = pruneExpired(readCache())
  return (cache[scopeKey] ?? []).map((entry) => entry.event)
}
